import type { Observation } from '@medplum/fhirtypes';

import {
  DEFAULT_MEASURE_CATALOG,
  type MeasureDef,
  matchCatalogMeasure,
  measureToken,
} from '@eluve/measure-catalog';

import { isPendingReview, isRetracted } from './review-markers';

/**
 * Page-side grouping of a patient's Observations into per-measure series
 * (ENG-5366 scope §2). Pure derivations over the fetched set — no fetching,
 * no React. The renderer owns single-resource presentation; this module owns
 * the aggregation a per-measure row needs (the walker renders one view per
 * resource, so a grouped row is deliberately not a registry view).
 */

/** One observation's contribution to a measure's numeric series. */
export type SeriesPoint = {
  observation: Observation;
  value: number;
  /** True when the value was corrected by a reviewer (status `amended`). */
  amended: boolean;
};

export type ObservationGroup = {
  /**
   * Stable routing key for the group: `system|code` of the matched coding
   * (catalog token when catalog-matched), `text:<label>` for uncoded
   * observations with a text label, `id:<id>` as the last resort.
   */
  token: string;
  /** Catalog definition when the group matches a tracked measure. */
  measure: MeasureDef | undefined;
  label: string;
  unit: string | undefined;
  /** Latest recorded method text, when present. */
  method: string | undefined;
  /** Every non-retracted observation in the group, oldest first. */
  observations: Observation[];
  /** Validated numeric points (not pending, not retracted), oldest first. */
  series: SeriesPoint[];
  /** Observations still awaiting review, oldest first. */
  pending: Observation[];
  /** Newest non-retracted observation — the drawer's detail subject. */
  latest: Observation;
};

export type GroupedObservations = {
  /** Catalog-matched groups, in catalog order. */
  measures: ObservationGroup[];
  /** Everything else, newest-activity first. */
  others: ObservationGroup[];
};

/** The sortable clinical time of an observation (effective, then issued). */
export function observationSortKey(observation: Observation): string {
  return (
    observation.effectiveDateTime ??
    observation.effectivePeriod?.start ??
    observation.issued ??
    ''
  );
}

/**
 * "34%", "9 cm", "3" — UCUM annotation units like `{score}` are display
 * noise and render as the bare number.
 */
export function formatMeasureValue(
  value: number,
  unit: string | undefined,
): string {
  if (!unit || unit.startsWith('{')) return String(value);
  if (unit === '%') return `${value}%`;
  return `${value} ${unit}`;
}

export function observationNumericValue(
  observation: Observation,
): number | undefined {
  const value = observation.valueQuantity?.value;
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

/** Display unit for a group: catalog unit, else first quantity unit seen. */
function groupUnit(
  measure: MeasureDef | undefined,
  observations: Observation[],
): string | undefined {
  const observed = observations.find(
    (o) => o.valueQuantity?.unit ?? o.valueQuantity?.code,
  );
  return (
    observed?.valueQuantity?.unit ??
    observed?.valueQuantity?.code ??
    measure?.unit
  );
}

function groupLabel(
  measure: MeasureDef | undefined,
  observation: Observation,
): string {
  return (
    measure?.label ??
    observation.code?.text ??
    observation.code?.coding?.[0]?.display ??
    'Observation'
  );
}

type GroupMatch = { token: string; measure: MeasureDef | undefined };

function matchGroup(
  observation: Observation,
  catalog: readonly MeasureDef[],
): GroupMatch {
  for (const coding of observation.code?.coding ?? []) {
    const measure = matchCatalogMeasure(catalog, coding);
    if (measure) return { token: measureToken(measure), measure };
  }
  const coded = observation.code?.coding?.find((c) => c.system && c.code);
  if (coded)
    return { token: `${coded.system}|${coded.code}`, measure: undefined };
  if (observation.code?.text) {
    return { token: `text:${observation.code.text}`, measure: undefined };
  }
  return { token: `id:${observation.id ?? 'unknown'}`, measure: undefined };
}

/**
 * Group a patient's observations by measure/code. Retracted
 * (`entered-in-error`) observations are dropped entirely — they are
 * dismissed drafts or superseded runs, not clinical history.
 */
export function groupPatientObservations(
  observations: readonly Observation[],
  catalog: readonly MeasureDef[] = DEFAULT_MEASURE_CATALOG,
): GroupedObservations {
  const byToken = new Map<
    string,
    { match: GroupMatch; observations: Observation[] }
  >();

  for (const observation of observations) {
    if (isRetracted(observation)) continue;
    const match = matchGroup(observation, catalog);
    const existing = byToken.get(match.token);
    if (existing) {
      existing.observations.push(observation);
    } else {
      byToken.set(match.token, { match, observations: [observation] });
    }
  }

  const groups: ObservationGroup[] = [];
  for (const { match, observations: members } of byToken.values()) {
    const ordered = [...members].sort((a, b) =>
      observationSortKey(a).localeCompare(observationSortKey(b)),
    );
    const pending = ordered.filter(isPendingReview);
    const series: SeriesPoint[] = [];
    for (const observation of ordered) {
      if (isPendingReview(observation)) continue;
      const value = observationNumericValue(observation);
      if (value === undefined) continue;
      series.push({
        observation,
        value,
        amended: observation.status === 'amended',
      });
    }
    const latest = ordered[ordered.length - 1];
    if (!latest) continue;
    const methodSource = [...ordered]
      .reverse()
      .find((o) => o.method?.text ?? o.method?.coding?.[0]?.display);
    groups.push({
      token: match.token,
      measure: match.measure,
      label: groupLabel(match.measure, latest),
      unit: groupUnit(match.measure, ordered),
      method:
        methodSource?.method?.text ??
        methodSource?.method?.coding?.[0]?.display,
      observations: ordered,
      series,
      pending,
      latest,
    });
  }

  const catalogOrder = new Map(catalog.map((m, index) => [m.key, index]));
  const measures = groups
    .filter((group) => group.measure)
    .sort(
      (a, b) =>
        (catalogOrder.get(a.measure?.key ?? '') ?? 0) -
        (catalogOrder.get(b.measure?.key ?? '') ?? 0),
    );
  const others = groups
    .filter((group) => !group.measure)
    .sort((a, b) =>
      observationSortKey(b.latest).localeCompare(observationSortKey(a.latest)),
    );

  return { measures, others };
}

/** Find a group by its routing token. */
export function findGroupByToken(
  grouped: GroupedObservations,
  token: string | null,
): ObservationGroup | undefined {
  if (!token) return undefined;
  return [...grouped.measures, ...grouped.others].find(
    (group) => group.token === token,
  );
}
